import React from 'react';
import Tabs from 'antd/lib/tabs';
import Form from 'antd/lib/form';
import Typography from 'antd/lib/typography';
import TextArea from 'antd/lib/input/TextArea';
import { AppStoreType, MetadataDisallowedTokens } from '../types';
import { getIsAppleAppStore, getMetadataReleaseNotesMaxLength, validatePattern } from '../helpers';
import { DefaultMetadataLocale } from '@/types/stepConfig';
import Flex from 'antd/lib/flex';
import styles from './styles/settingsStyles';

interface WhatsNewFormItemProps {
    appStoreType: AppStoreType;
    shouldDisableMetadataEditing?: boolean;
    shouldRequireReleaseNotes?: boolean;
    placeholder?: string;
    name?: string | string[];
}

export const createWhatsNewFormItem = ({
    name = 'whatsNew',
    shouldRequireReleaseNotes = true,
    appStoreType,
    shouldDisableMetadataEditing = false,
    placeholder = "Add information about what's new in this release.",
}: WhatsNewFormItemProps) => {
    const releaseNotesMaxLength = getMetadataReleaseNotesMaxLength(appStoreType);

    return (
        <Form.Item
            name={name}
            rules={[
                {
                    max: releaseNotesMaxLength,
                    message: `Should be less than ${releaseNotesMaxLength} characters`,
                },
                { required: shouldRequireReleaseNotes, message: 'Please enter a release note' },
                {
                    validator: (_, value) => {
                        const pattern = value as string;
                        if (shouldRequireReleaseNotes) {
                            return validatePattern(pattern, 'Release notes', [], MetadataDisallowedTokens);
                        }

                        return Promise.resolve();
                    },
                },
            ]}
        >
            <TextArea
                disabled={shouldDisableMetadataEditing}
                placeholder={placeholder}
                style={{ minWidth: 500, height: 250, position: 'relative' }}
            />
        </Form.Item>
    );
};

const PROMO_TEXT_MAX_LENGTH = 170;

const createPromoTextFormItem = (name: string | string[]) => {
    return (
        <Form.Item
            name={name}
            rules={[
                {
                    max: PROMO_TEXT_MAX_LENGTH,
                    message: `Promotional text must be ${PROMO_TEXT_MAX_LENGTH} characters or less.`,
                },
            ]}
        >
            <TextArea
                placeholder="Add default promotional text."
                style={{ minWidth: 500, height: 120, position: 'relative' }}
            />
        </Form.Item>
    );
};

interface MetadataDefaultFormsProps {
    appStoreType: AppStoreType;
    defaultMetadata: DefaultMetadataLocale[];
}

export const MetadataDefaultFields: React.FC<MetadataDefaultFormsProps> = ({ appStoreType, defaultMetadata }) => {
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const whatsNewTextOrReleaseNotesText = isAppleAppStore ? "'What's new' text" : 'release notes';

    return (
        <Flex vertical>
            <Typography.Text style={{ ...styles.appSettingsSectionTitleStyle, paddingBottom: 8, paddingTop: 8 }}>
                Default {whatsNewTextOrReleaseNotesText}
            </Typography.Text>

            <Tabs destroyInactiveTabPane={false}>
                {defaultMetadata.map((defaultMetadataLocale) => {
                    return (
                        <Tabs.TabPane
                            forceRender
                            key={defaultMetadataLocale.locale}
                            tab={<Typography.Text>{defaultMetadataLocale.displayLocale}</Typography.Text>}
                        >
                            {createWhatsNewFormItem({
                                appStoreType,
                                name: ['defaultMetadataByLocale', defaultMetadataLocale.locale, 'whatsNew'],
                                shouldRequireReleaseNotes: false,
                            })}
                        </Tabs.TabPane>
                    );
                })}
            </Tabs>

            {isAppleAppStore && (
                <>
                    <Typography.Text
                        style={{ ...styles.appSettingsSectionTitleStyle, paddingBottom: 8, paddingTop: 8 }}
                    >
                        Default promotional text
                    </Typography.Text>

                    <Tabs destroyInactiveTabPane={false}>
                        {defaultMetadata.map((defaultMetadataLocale) => {
                            return (
                                <Tabs.TabPane
                                    forceRender
                                    key={defaultMetadataLocale.locale}
                                    tab={<Typography.Text>{defaultMetadataLocale.displayLocale}</Typography.Text>}
                                >
                                    {createPromoTextFormItem([
                                        'defaultMetadataByLocale',
                                        defaultMetadataLocale.locale,
                                        'promoText',
                                    ])}
                                </Tabs.TabPane>
                            );
                        })}
                    </Tabs>
                </>
            )}
        </Flex>
    );
};
